import { Api } from '@deepcloud/api-base'
import { mergeConfig } from '@deepcloud/http-client'
import { withQueryParams } from '../../../shared'
import type {
  HttpClient,
  HttpClientRequestConfig,
} from '@deepcloud/http-client'
import type {
  JiraCommentOrderUpdate,
  JiraCommentsResponse,
  JiraFilterOption,
  JiraIssueDetail,
  JiraProject,
  JiraSearchGetIssueCommentsParams,
  JiraSearchGetIssueParams,
  JiraSearchListIssueTypesParams,
  JiraSearchListStatusesParams,
  JiraSearchRequest,
  JiraSearchResponse,
  JiraSearchSearchParams,
} from '../../@types'

export class JiraSearchAPI extends Api {
  constructor(httpClient: HttpClient) {
    super(httpClient)
  }

  /**
   * Search
   * -------------------
   * Errors:
   * - 400 Die JQL-Abfrage wurde von uns abgelehnt (Syntax, Feld, Operator, Grenze). → {@link JqlErrorResponse}
   * - 422 Jira hat die Abfrage abgelehnt (unbekannter Wert oder Metadatum). → {@link JqlErrorResponse}
   */
  async search(
    payload: JiraSearchRequest,
    params?: JiraSearchSearchParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = withQueryParams(`/jira-search/search`, params)

    return this.httpClient.post<JiraSearchResponse>(path, payload, config)
  }

  /**
   * List Projects
   * -------------------
   * Errors:
   * - none documented
   */
  async listProjects(config?: HttpClientRequestConfig) {
    const path = `/jira-search/projects`

    return this.httpClient.get<JiraProject[]>(path, config)
  }

  /**
   * List Issue Types
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async listIssueTypes(
    params?: JiraSearchListIssueTypesParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/jira-search/issue-types`

    return this.httpClient.get<JiraFilterOption[]>(
      path,
      mergeConfig({ params }, config),
    )
  }

  /**
   * List Statuses
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async listStatuses(
    params?: JiraSearchListStatusesParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/jira-search/statuses`

    return this.httpClient.get<JiraFilterOption[]>(
      path,
      mergeConfig({ params }, config),
    )
  }

  /**
   * Get Issue
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async getIssue(
    key: string,
    params?: JiraSearchGetIssueParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/jira-search/issue/${key}`

    return this.httpClient.get<JiraIssueDetail>(
      path,
      mergeConfig({ params }, config),
    )
  }

  /**
   * Get Issue Comments
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async getIssueComments(
    key: string,
    params?: JiraSearchGetIssueCommentsParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/jira-search/issue/${key}/comments`

    return this.httpClient.get<JiraCommentsResponse>(
      path,
      mergeConfig({ params }, config),
    )
  }

  /**
   * Set Comment Order
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async setCommentOrder(
    payload: JiraCommentOrderUpdate,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/jira-search/comment-order`

    return this.httpClient.put<never>(path, payload, config)
  }

  /**
   * Follow Issue
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async followIssue(key: string, config?: HttpClientRequestConfig) {
    const path = `/jira-search/follows/${key}`

    return this.httpClient.put<never>(path, undefined, config)
  }

  /**
   * Unfollow Issue
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async unfollowIssue(key: string, config?: HttpClientRequestConfig) {
    const path = `/jira-search/follows/${key}`

    return this.httpClient.delete<never>(path, config)
  }
}
